import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../context/breadcrumb.context'
import { WorkspaceShell } from '../../../../shared/sso-workspace/workspace-shell'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime } from '../../../../shared/sso-workspace/format'
import { useAuditCapabilities } from '../../../../shared/audit/use-audit-capabilities'
import { getEntries } from '../../../../shared/audit/audit-api'
import { entryPath } from '../../../../shared/audit/record-links'
import { ENTRY_STATE } from '../../../../shared/audit/entry-route'
import { byline } from '../../../../shared/audit/entry-format'
import {
  IconAlertConnection, IconAlertRoute, IconAlertTarget, IconEdit, IconEnvelope, IconNext, IconWebhook
} from '../../../../shared/icon/icon'
import { SlackMark } from '../../../../shared/icon/slack-mark'
import { listRuns } from './alert-routing-api'
import { CONNECTIONS_PAGE, ROUTES_PAGE, TARGETS_PAGE, runHref } from './paths'
import { ALERT_STATUS, SEVERITY, WordPill } from './run-words'
import { SETUP_PILL } from './setup-words'
import './alert-routing.scss'

// Alert routing's record pages (owner 2026-10-30: the drawers "fall short big
// time" next to the SSO connection pages) are the SSO connection workspace's
// anatomy (provider-workspace.js): the crumb bar ("Alert targets > name",
// History on its right), the header (icon, name, a muted subtitle, the status
// pill, the primary action and a ⋮), the summary strip, the section nav with
// its dots, and the sections as cards.

// The crumb before the record's own: its list page.
export const CRUMBS = {
  routes: { url: ROUTES_PAGE, text: 'Alert routes', Icon: IconAlertRoute },
  targets: { url: TARGETS_PAGE, text: 'Alert targets', Icon: IconAlertTarget },
  connections: { url: CONNECTIONS_PAGE, text: 'Alert connections', Icon: IconAlertConnection }
}

// The page: the crumb bar, the pane that scrolls itself, the workspace shell
// (header, strip, nav). crumb is the record's own crumb (its name).
export const AlertWorkspace = ({ list, crumb, children, ...shell }) =>
  <BreadcrumbView parent={CRUMBS[list]} Icon={IconEdit} text={crumb}>
    <div className="sw-pane p-3" data-testid="workspace-pane">
      <WorkspaceShell mode="staff" {...shell}>{children}</WorkspaceShell>
    </div>
  </BreadcrumbView>

// A page with no sections to jump between yet (a new target's first step,
// loading, a record that couldn't be loaded): the same frame and header, one
// column.
export const AlertPlainPage = ({ list, crumb, title, subtitle, logo, children }) =>
  <BreadcrumbView parent={CRUMBS[list]} Icon={IconEdit} text={crumb}>
    <div className="sw-pane p-3" data-testid="workspace-pane">
      <div className="sw-workspace pb-5">
        <header className="sw-head d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="d-flex align-items-center gap-3 min-w-0">
            {logo}
            <div className="min-w-0">
              <h1 className="sw-title">{title}</h1>
              {subtitle && <div className="sw-sub">{subtitle}</div>}
            </div>
          </div>
        </header>
        <div className="ar-plain">{children}</div>
      </div>
    </div>
  </BreadcrumbView>

// A section card (the workspace's .sw-section): its title and one line on
// what it is for, and its own actions on the right (+ New Slack target).
export const ArSection = ({ id, title, lede, actions, children }) =>
  <section className="sw-section ar-wsection" id={id} aria-labelledby={`${id}-title`}>
    <div className="ar-wsection-head">
      <div className="min-w-0">
        <h2 id={`${id}-title`}>{title}</h2>
        {lede && <p className="sw-lede">{lede}</p>}
      </div>
      {actions && <div className="ar-wsection-actions">{actions}</div>}
    </div>
    {children}
  </section>

// A sub-list's heading inside a section, with a link on its right.
export const SubHead = ({ title, aside }) =>
  <div className="sw-subh ar-subh"><h3>{title}</h3>{aside}</div>

// The header's icon tile: the target's type, or the record's own icon.
const TYPE_ICONS = { slack_app: SlackMark, slack: SlackMark, email: IconEnvelope, webhook: IconWebhook }
export const TypeLogo = ({ type, Icon }) => {
  const Glyph = Icon || TYPE_ICONS[type] || IconAlertTarget
  return <div className="sw-logo ar-logo" aria-hidden="true" data-testid="sw-logo"><Glyph /></div>
}

// "Setup incomplete", amber: a step is missing (setup-words.js).
export const SetupPill = () => <Pill tone="warn">{SETUP_PILL}</Pill>

// The notice that says what is missing and holds the button that does it.
export const SetupNotice = ({ setup, onAction, disabled, title }) =>
  <div className="sw-notice warn ar-setup" role="status" data-testid="setup-notice">
    <div className="ar-setup-row">
      <span>{setup.text}</span>
      {onAction && <button type="button" className="btn sw-btn btn-sm" disabled={disabled} title={title} onClick={onAction}>{setup.action}</button>}
    </div>
  </div>

// "2 hours ago", the exact local time on hover.
export const When = ({ at }) => (at ? <span title={formatDateTime(at)}>{formatAgo(at)}</span> : null)

// Rows that each lead somewhere (owner 2026-10-30: the activity lists were
// dead text; SCIM activity, the audit and request logs lead to a detail): the
// whole row is the link. items: { key, to (a path or a location), tone (the
// dot: ok, warn, bad; null a plain one; undefined none), title, sub, meta
// (pills), when (its time, right-aligned) }.
export const LinkRows = ({ label, items }) =>
  <ul className="ar-rows" aria-label={label}>
    {items.map(item =>
      <li key={item.key}>
        <Link className="ar-rowlink" to={item.to}>
          {item.tone !== undefined && <span className="ar-dot" data-tone={item.tone || 'none'} aria-hidden="true" />}
          <span className="ar-rowmain">
            <span className="ar-rowtitle">{item.title}</span>
            {item.sub && <span className="ar-rowsub">{item.sub}</span>}
          </span>
          {item.meta && <span className="ar-rowmeta">{item.meta}</span>}
          {item.when !== undefined && <span className="ar-rowwhen">{item.when}</span>}
          <IconNext className="ar-rowgo" />
        </Link>
      </li>)}
  </ul>

// Nothing in a list yet: one muted line in the list's place.
export const EmptyLine = ({ children }) => <p className="ar-empty">{children}</p>

// An alert as a row (a target's deliveries, a workspace's activity): its
// title, what raised it, its status and when; it opens the alert's detail,
// which comes back here when closed (returnTo).
const RUN_TONE = { delivered: 'ok', partial: 'warn', unrouted: 'warn', failed: 'bad', expired: 'bad' }
export const runItem = (run, returnTo) => ({
  key: `run-${run.id}`,
  to: { pathname: runHref(run.id), state: { returnTo } },
  tone: RUN_TONE[run.status] || null,
  title: run.isTest ? 'Test message' : run.title || run.triggerLabel || 'Alert',
  sub: run.isTest ? 'From Send test' : [run.triggerLabel, (SEVERITY[run.severity] || {}).label].filter(Boolean).join(' · '),
  meta: <WordPill words={ALERT_STATUS} value={run.status} />,
  when: <When at={run.raisedAt} />
})

// An audit entry as a row: who did what, opening the entry (the audit log's
// permalink drawer), as SCIM's and SSO's activity lead to theirs.
const EVENT_TONE = { 'channel_connection.disconnected': 'warn', 'channel_connection.broken': 'bad' }
const capitalized = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
export const eventItem = (entry, sys) => ({
  key: `event-${entry.id}`,
  to: { pathname: entryPath(entry.id, sys), state: ENTRY_STATE },
  tone: EVENT_TONE[entry.action] || null,
  title: capitalized(entry.sentence || entry.action),
  sub: byline(entry, { sys }),
  when: <When at={entry.occurredAt} />
})

// The latest alerts sent to any of these targets (GET /runs?targetID= per
// target: the API filters by one), newest first, each once. undefined while
// loading (or while the ids are), null when it failed.
export const RECENT = 6
export const useRecentRuns = (targetIDs, reloadKey) => {
  const [runs, setRuns] = useState(undefined)
  const ids = targetIDs === undefined ? undefined : targetIDs.join(',')
  useEffect(() => {
    if (ids === undefined) return undefined
    if (!ids) { setRuns([]); return undefined }
    let live = true
    Promise.all(ids.split(',').map(id => listRuns({ targetID: id, limit: RECENT }))).then(pages => {
      if (!live) return
      const seen = new Map()
      pages.forEach(page => ((page && page.items) || []).forEach(run => seen.set(run.id, run)))
      setRuns([...seen.values()].sort((a, b) => Date.parse(b.raisedAt) - Date.parse(a.raisedAt)).slice(0, RECENT))
    }, () => live && setRuns(null))
    return () => { live = false }
  }, [ids, reloadKey])
  return runs
}

// A record's latest audit entries (content's record= read), for a reader of
// the system audit log; null for anyone else, or when the read fails (the
// History button stays the way in).
export const useRecordEvents = (type, id, reloadKey) => {
  const caps = useAuditCapabilities()
  const [events, setEvents] = useState(undefined)
  useEffect(() => {
    if (!caps.loaded || !id) return undefined
    if (!caps.sysAuditLog) { setEvents(null); return undefined }
    let live = true
    getEntries({ record: `${type}:${id}`, limit: RECENT }).then(page => live && setEvents((page && page.items) || []), () => live && setEvents(null))
    return () => { live = false }
  }, [caps.loaded, caps.sysAuditLog, type, id, reloadKey])
  return { events, sys: Boolean(caps.sysAuditLog) }
}
